import React, { useEffect, useRef, useState } from 'react';
import { webRTCService } from '../services/webrtc';
import { useAuth } from '../hooks/useAuth';
import { Row } from '../components/Layout';
import { getImageUrl } from '../services/api';
import api from '../services/api';
import { socketService } from '../services/socket';
import StreamGifts, { Gift } from '../components/StreamGifts';

interface WatchStreamPageProps {
    streamId: string;
    onBack: () => void;
    onViewProfile?: (userId: string) => void;
}

const WatchStreamPage: React.FC<WatchStreamPageProps> = ({ streamId, onBack, onViewProfile }) => {
    const { user } = useAuth();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [connected, setConnected] = useState(false);
    const [streamData, setStreamData] = useState<any>(null);
    const [showGiftPanel, setShowGiftPanel] = useState(false);

    // UI states
    const [msgs, setMsgs] = useState<{ user: string, userId?: string, msg?: string, color: string, avatar?: string, isGift?: boolean, gift?: Gift }[]>([]);
    const [input, setInput] = useState('');
    const [liked, setLiked] = useState(false);
    const [likes, setLikes] = useState(0);
    const [heart, setHeart] = useState(false);
    
    // Guest states
    const [isGuest, setIsGuest] = useState(false);
    const [guestStream, setGuestStream] = useState<MediaStream | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
    const guestVideoRef = useRef<HTMLVideoElement>(null);

    const chatRef = useRef<HTMLDivElement>(null);
    const [viewerCount, setViewerCount] = useState(0);

    useEffect(() => {
        if (!streamId) return;
        
        // Fetch stream data
        api.get('/streams').then(res => {
            const stream = res.data.find((s: any) => s._id === streamId || s.streamKey === streamId);
            if (stream) setStreamData(stream);
        }).catch(err => console.error(err));

        const socket = socketService.getSocket();
        if (socket) {
            if (!socket.connected) socket.connect();
            
            socket.emit('join-stream-room', streamId);
            
            socket.on('streamMessage', (data: any) => {
                setMsgs(prev => [...prev.slice(-49), { 
                    user: data.user?.username || 'Kullanıcı', 
                    userId: data.user?._id || data.user?.id,
                    msg: data.text, 
                    color: '#fff',
                    avatar: data.user?.profileImage,
                    isGift: data.isGift,
                    gift: data.gift
                }]);
                setTimeout(() => chatRef.current?.scrollTo({ top: 9999, behavior: 'smooth' }), 50);
            });

            socket.on('viewerCountUpdate', (count: number) => {
                setViewerCount(count);
            });

            socket.on('guestAccepted', async (data: any) => {
                if (data.streamId === streamId) {
                    setIsGuest(true);
                    // Start local camera
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({
                            video: { facingMode: 'user' },
                            audio: true
                        });
                        setGuestStream(stream);
                        if (guestVideoRef.current) {
                            guestVideoRef.current.srcObject = stream;
                        }
                        // Broadcast as guest without leaving/rejoining the Agora channel
                        await webRTCService.publishAsGuest(stream);
                    } catch (err) {
                        console.error('Guest camera error:', err);
                        alert('Kamera açılamadı!');
                    }
                }
            });

            socket.on('guestRejected', () => {
                alert('Yayıncı misafir isteğinizi reddetti.');
            });

            socket.on('guestRemoved', async () => {
                setIsGuest(false);
                alert('Yayından çıkarıldınız.');
                if (guestStream) {
                    guestStream.getTracks().forEach(t => t.stop());
                    setGuestStream(null);
                }
                await webRTCService.unpublishLocalAudio(); 
                window.location.reload(); 
            });
        }

        return () => {
            if (socket) {
                socket.off('streamMessage');
                socket.off('guestAccepted');
                socket.off('guestRejected');
                socket.off('viewerCountUpdate');
                socket.emit('leave-stream-room', streamId);
            }
        };
    }, [streamId]);

    useEffect(() => {
        if (!streamId || !user) return;
        
        webRTCService.onRemoteStream = (socketId, stream, incomingUserId) => {
            if (incomingUserId !== (user?.id || (user as any)?._id)) {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    setConnected(true);
                    videoRef.current.play().catch(err => {
                        console.warn("[Stream] Auto-play prevented", err);
                    });
                }
            }
        };

        webRTCService.watchStream(streamId, user.id || (user as any)._id || 'viewer');

        return () => {
            webRTCService.stopViewing().catch(err => console.error(err));
            if (guestStream) {
                guestStream.getTracks().forEach(t => t.stop());
            }
        };
    }, [streamId, user]);

    const send = () => {
        if (!input.trim() || !user) return;
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('streamMessage', {
                streamId,
                text: input,
                user: { _id: user.id || (user as any)._id, username: user.username, profileImage: user.profileImage }
            });
            setInput('');
        }
    };

    const like = () => {
        setLiked(l => !l);
        setLikes(c => liked ? c - 1 : c + 1);
        setHeart(true);
        setTimeout(() => setHeart(false), 800);
    };

    const handleSendGift = (gift: Gift) => {
        if (!user) return;
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('streamMessage', {
                streamId,
                isGift: true,
                gift: gift,
                user: { _id: user.id || (user as any)._id, username: user.username, profileImage: user.profileImage }
            });
        }
    };

    const requestGuest = () => {
        if (!streamData?.streamer?._id || !user) return;
        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('guestRequest', {
                streamId,
                broadcasterId: streamData.streamer._id,
                user: { _id: user.id || (user as any)._id, username: user.username, profileImage: user.profileImage }
            });
            alert('Misafirlik isteği gönderildi.');
        }
    };

    const handleFlipGuestCamera = async () => {
        if (!guestStream) return;
        const newMode = facingMode === 'user' ? 'environment' : 'user';
        setFacingMode(newMode);
        try {
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: newMode }
            });
            const newTrack = newStream.getVideoTracks()[0];
            if (newTrack) {
                webRTCService.replaceVideoTrack(newTrack);
                const oldTrack = guestStream.getVideoTracks()[0];
                if (oldTrack) {
                    guestStream.removeTrack(oldTrack);
                    oldTrack.stop();
                }
                guestStream.addTrack(newTrack);
                if (guestVideoRef.current) {
                    guestVideoRef.current.srcObject = guestStream;
                }
            }
        } catch (err) {
            console.error('Kamera çevirme hatası', err);
            setFacingMode(facingMode);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, height: '100%', animation: 'slideL .4s ease', background: '#000', overflow: 'hidden', fontFamily: "'Inter', 'DM Sans', sans-serif" }}>
            <div style={{ position: 'absolute', inset: 0, transition: 'opacity 1s', background: `radial-gradient(circle at 50% 50%, #2A0845, #11001C)` }}>
                <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: connected ? 1 : 0.6 }} />
                {!connected && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 48, height: 48, border: '4px solid rgba(255,255,255,.1)', borderTopColor: '#FF2A85', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        <div style={{ marginTop: 20, fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.8)', letterSpacing: 0.5 }}>Yayına bağlanılıyor...</div>
                    </div>
                )}
            </div>

            {/* Guest PiP Video */}
            {isGuest && (
                <div style={{ 
                    position: 'absolute', top: 120, right: 16, width: 100, height: 150, 
                    borderRadius: 12, overflow: 'hidden', border: '2px solid #FF2A85', zIndex: 15,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
                }}>
                    <video ref={guestVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
            )}

            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, padding: '45px 16px 20px',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 10
            }}>
                <div 
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                    onClick={() => {
                        if (streamData?.streamer?._id && onViewProfile) {
                            onViewProfile(streamData.streamer._id);
                        }
                    }}
                >
                    <div style={{ position: 'relative' }}>
                        <img src={streamData?.streamer?.profileImage ? getImageUrl(streamData.streamer.profileImage) : "https://i.pravatar.cc/150?img=32"} alt="Avatar" style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid #FF2A85', objectFit: 'cover' }} />
                        <span className="live-badge" style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', padding: '2px 6px', borderRadius: 10, background: '#FF2A85', color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: 0.5, border: '1px solid #11001C' }}>CANLI</span>
                    </div>
                    <div style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: 0.2 }}>{streamData?.streamer?.username || 'Yayıncı'}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.8)', fontWeight: 500 }}>{viewerCount} İzleyici</div>
                    </div>
                </div>
                
                <div style={{ display: 'flex', gap: 10 }}>
                    {!isGuest && (
                        <button onClick={requestGuest} style={{ background: 'rgba(255,42,133,0.8)', border: 'none', borderRadius: 20, padding: '6px 12px', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                            Misafir Ol
                        </button>
                    )}
                    {isGuest && (
                        <button onClick={handleFlipGuestCamera} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '50%', width: 36, height: 36, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                            🔄
                        </button>
                    )}
                    <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>✕</button>
                </div>
            </div>

            <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(17,0,28,0.95) 0%, rgba(17,0,28,0.7) 40%, transparent 100%)',
                display: 'flex', flexDirection: 'column', zIndex: 10, paddingTop: 60
            }}>
                <div ref={chatRef} style={{ maxHeight: '30vh', overflowY: 'auto', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }} className="hide-scrollbar">
                    {msgs.map((m, i) => (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', cursor: 'pointer' }} onClick={() => m.userId && onViewProfile && onViewProfile(m.userId)}>
                            <div style={m.isGift ? {
                                background: 'linear-gradient(135deg, rgba(255,42,133,0.9), rgba(233,64,87,0.9))', backdropFilter: 'blur(10px)',
                                padding: '8px 16px', borderRadius: 20, borderTopLeftRadius: 4,
                                border: '1px solid rgba(255,255,255,0.2)', maxWidth: '85%',
                                display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 4px 15px rgba(255,42,133,0.4)'
                            } : { 
                                background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', 
                                padding: '8px 12px', borderRadius: 16, borderTopLeftRadius: 4,
                                border: '1px solid rgba(255,255,255,0.05)', maxWidth: '85%',
                                display: 'flex', alignItems: 'center', gap: 8
                            }}>
                                {m.avatar && <img src={getImageUrl(m.avatar)} alt="avatar" style={{width: 24, height: 24, borderRadius: '50%', objectFit: 'cover'}} />}
                                <div>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: m.isGift ? '#FFF0F5' : m.color, marginRight: 8, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{m.user}</span>
                                    {m.isGift && m.gift ? (
                                        <span style={{ fontSize: 15, color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            sana <span style={{ fontSize: 20 }}>{m.gift.icon}</span> {m.gift.name} gönderdi!
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: 14, color: '#F8F8F8', lineHeight: 1.4, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{m.msg}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ position: 'absolute', bottom: 20, left: 16, right: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{
                        flex: 1, height: 44, background: 'rgba(0,0,0,0.4)', borderRadius: 22, display: 'flex', alignItems: 'center', padding: '0 16px', border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <input type="text" placeholder="Yorum ekle..." value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && send()}
                            style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', outline: 'none', fontSize: 14 }}
                        />
                        <button onClick={send} style={{
                            background: 'linear-gradient(135deg, #00E5CC, #0099FF)', width: 32, height: 32, borderRadius: 16,
                            border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s', cursor: 'pointer'
                        }}>
                            ➤
                        </button>
                    </div>
                    
                    <button onClick={() => setShowGiftPanel(true)} style={{ 
                        width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #FF2A85 0%, #E94057 100%)', 
                        border: 'none', color: '#fff', fontWeight: 700, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(255,42,133,0.4)', cursor: 'pointer'
                    }}>
                        🎁
                    </button>

                    <button onClick={like} style={{
                        width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)',
                        color: liked ? '#FF2A85' : '#fff', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s',
                        transform: liked ? 'scale(1.1)' : 'scale(1)'
                    }}>
                        {liked ? ' 💖' : '🤍'}
                    </button>
                </div>

                <StreamGifts
                    isOpen={showGiftPanel}
                    onClose={() => setShowGiftPanel(false)}
                    onSendGift={handleSendGift}
                    balance={user?.coins || 100}
                />
                
                {heart && (
                    <div style={{ position: 'absolute', bottom: 100, right: 30, fontSize: 40, animation: 'floatUp 1s ease-out forwards', pointerEvents: 'none', zIndex: 100, opacity: 0 }}>♥️</div>
                )}
                
                <style dangerouslySetInnerHTML={{__html: `
                    @keyframes floatUp {
                        0% { transform: translateY(0) scale(0.8); opacity: 1; }
                        50% { transform: translateY(-50px) scale(1.2); opacity: 0.8; }
                        100% { transform: translateY(-100px) scale(1); opacity: 0; }
                    }
                `}} />
            </div>
        </div>
    );
};

export default WatchStreamPage;
